export type AnalyticsEventName =
  | "page_view" | "cta_click" | "signup_started" | "signup_completed" | "login" | "organisation_created"
  | "first_requisition" | "first_rfq" | "supplier_invited" | "quotation_received"
  | "purchase_order_issued" | "supplier_profile_claimed" | "plan_upgraded" | "demo_requested"
  | "tool_started" | "tool_completed" | "tool_exported" | "template_downloaded"
  | "resource_signup_clicked" | "sector_cta_clicked" | "one_purchase_challenge_started"
  | "procurement_autopsy_requested" | "partner_kit_requested" | "supplier_referral_clicked"
  | "founding_circle_viewed" | "founding_path_selected" | "founding_application_started" | "founding_application_submitted"
  | "advisor_page_viewed" | "advisor_kit_accessed" | "advisor_pilot_requested"
  | "iso_consultant_page_viewed" | "iso_consultant_interest_submitted"
  | "vertical_page_viewed" | "vertical_assessment_started" | "vertical_assessment_completed"
  | "vertical_tool_accessed" | "vertical_signup_started" | "vertical_signup_completed"
  | "vertical_pilot_interest_submitted" | "control_gap_viewed";
export type AnalyticsConsent = "unknown" | "denied" | "granted";
const CONSENT_KEY = "raqoz_analytics_consent";
const FIRST_TOUCH_KEY = "raqoz_first_touch_v1";
const LAST_TOUCH_KEY = "raqoz_last_touch_v1";
export const CONSENT_EVENT = "raqoz:analytics-consent";
export const MEASUREMENT_ID = String(import.meta.env.VITE_GA_MEASUREMENT_ID || "G-NR5922J3HW").trim();
const forbidden = /(document|quote|specification|attachment|bank|invoice|account|tax|gst|purchase[_-]?order|requisition|supplier.*email|payload|description|amount|value)/i;
let loaded = false;

declare global { interface Window { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void; } }

export type PublicAttribution = {
  capturedAt: string;
  landingPath: string;
  source: string;
  medium?: string;
  campaign?: string;
  referral?: string;
};

function safeAttributionValue(value: string | null): string | undefined {
  const clean = String(value || "").trim().slice(0, 64);
  return /^[a-zA-Z0-9._~-]+$/.test(clean) ? clean : undefined;
}

function readAttribution(key: string): PublicAttribution | null {
  if (typeof window === "undefined") return null;
  try {
    const value = JSON.parse(window.localStorage.getItem(key) || "null") as PublicAttribution | null;
    return value && typeof value.source === "string" && typeof value.landingPath === "string" ? value : null;
  } catch {
    return null;
  }
}

export function attributionSnapshot(): Record<string, string> {
  const first = readAttribution(FIRST_TOUCH_KEY);
  if (!first) return {};
  return {
    first_source: first.source,
    first_medium: first.medium || "none",
    first_campaign: first.campaign || "none",
    first_referral: first.referral || "none",
    first_landing_path: first.landingPath,
  };
}

export function capturePublicAttribution(): PublicAttribution | null {
  if (typeof window === "undefined" || analyticsConsent() !== "granted") return null;
  const search = new URLSearchParams(window.location.search);
  const source = safeAttributionValue(search.get("utm_source"));
  const medium = safeAttributionValue(search.get("utm_medium"));
  const campaign = safeAttributionValue(search.get("utm_campaign"));
  const referral = safeAttributionValue(search.get("ref")) || safeAttributionValue(search.get("referral"));
  const current: PublicAttribution = {
    capturedAt: new Date().toISOString(),
    landingPath: window.location.pathname.replace(/\/+$/, "") || "/",
    source: source || (referral ? "referral" : "direct"),
    ...(medium ? { medium } : {}),
    ...(campaign ? { campaign } : {}),
    ...(referral ? { referral } : {}),
  };
  try {
    if (!window.localStorage.getItem(FIRST_TOUCH_KEY)) window.localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(current));
    if (source || medium || campaign || referral || !window.localStorage.getItem(LAST_TOUCH_KEY)) window.localStorage.setItem(LAST_TOUCH_KEY, JSON.stringify(current));
  } catch {
    return null;
  }
  return readAttribution(FIRST_TOUCH_KEY);
}

export function analyticsConsent(): AnalyticsConsent {
  if (typeof window === "undefined") return "unknown";
  try {
    const value = window.localStorage.getItem(CONSENT_KEY);
    return value === "granted" || value === "denied" ? value : "unknown";
  } catch {
    return "unknown";
  }
}

export function setAnalyticsConsent(value: Exclude<AnalyticsConsent, "unknown">) {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(CONSENT_KEY, value); } catch { /* storage unavailable: fail safely */ }
  if (value === "granted") ensureDestination();
  if (value === "granted") capturePublicAttribution();
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: { consent: value } }));
}

export function ensureDestination(): boolean {
  if (typeof window === "undefined" || analyticsConsent() !== "granted") return false;
  if (!/^G-[A-Z0-9]+$/.test(MEASUREMENT_ID)) return false;
  if (loaded) return typeof window.gtag === "function";
  loaded = true;
  window.dataLayer = window.dataLayer || [];
  // gtag.js only interprets dataLayer entries that are `arguments` objects as commands.
  // Pushing a plain array silently drops every command, so no hits ever reach GA4.
  function gtagCommand(this: unknown) {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer?.push(arguments);
  }
  window.gtag = gtagCommand as unknown as (...args: unknown[]) => void;

  window.gtag("js", new Date());
  window.gtag("config", MEASUREMENT_ID, {
    anonymize_ip: true,
    allow_google_signals: false,
    send_page_view: false,
  });
  const script = document.createElement("script");
  script.async = true;
  script.src = "https://www.googletagmanager.com/gtag/js?id=" + encodeURIComponent(MEASUREMENT_ID);
  document.head.appendChild(script);
  return true;
}

export function sendMeasurementEvent(name: string, parameters: Record<string, string | number | boolean> = {}) {
  if (typeof window === "undefined" || analyticsConsent() !== "granted") return;
  if (!ensureDestination()) return;
  try { window.gtag?.("event", name, parameters); } catch { /* measurement must never break the page */ }
}

export function track(event: AnalyticsEventName, properties: Record<string, string | number | boolean | null> = {}) {
  if (typeof window === "undefined" || analyticsConsent() !== "granted") return;
  const safe = Object.fromEntries(Object.entries(properties).filter(([key, value]) => !forbidden.test(key) && (typeof value === "string" ? value.length <= 120 : true)));
  const attributed = { ...attributionSnapshot(), ...safe };
  const payload = { event, properties: attributed, occurredAt: new Date().toISOString() };
  window.dispatchEvent(new CustomEvent("raqoz:analytics", { detail: payload }));
  if (event !== "page_view") sendMeasurementEvent(event, attributed as Record<string, string | number | boolean>);
}
